import * as Sentry from '@sentry/browser';
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

import makeApiRequest from '../../utils/makeApiRequest';

import { API_ENDPOINTS } from '../../constants';
import Button from './Button';
import Header from './Header';

const StepTransferStarted = ({ customerId, onTransferFinished, onTransferIssue }) => {
  const [state, setState] = useState({
    checkingStatus: true,
    failed: false,
    resetting: false,
    resetError: false,
    resubmitted: false,
    resubmitting: false,
  });

  useEffect(() => {
    let intervalId;
    let isActive = true;

    const checkTransferStatus = async () => {
      try {
        const response = await makeApiRequest(API_ENDPOINTS.ACTIVATION_TRANSFER_STATUS, {
          CustomerId: customerId,
        });

        if (!isActive) {
          clearInterval(intervalId);
          return;
        }

        console.log(response);

        if (response.PortInStatus === 'Complete' || response.Status === 'Complete') {
          clearInterval(intervalId);
          setState((prev) => ({ ...prev, checkingStatus: false }));
          onTransferFinished(response.nextStep);
          return;
        }

        if (response.PortInStatus === 'ResolutionRequired') {
          if (response.PortInCode === '8D' && !state.resubmitted && !state.resubmitting) {
            clearInterval(intervalId);
            setState((prev) => ({ ...prev, resubmitting: true }));
            try {
              await handleZipResubmit(response);
              intervalId = setInterval(checkTransferStatus, 5000);
            } catch (error) {
              handleError(error, response);
            }
          } else {
            handleError(null, response);
          }
          return;
        }

        if (!['InProgress', 'Submit', 'New'].includes(response.Status)) {
          handleError(null, response);
        }
      } catch (error) {
        if (isActive) handleError(error);
      }
    };

    const handleError = (error, response) => {
      if (error) {
        console.error(error);
        Sentry.captureException(error);
      }
      if (response) {
        Sentry.captureException(`Transfer Status Failed | ${JSON.stringify(response)}`);
      }
      clearInterval(intervalId);
      setState((prev) => ({ ...prev, checkingStatus: false, failed: true }));
      onTransferIssue(response);
    };

    intervalId = setInterval(checkTransferStatus, 5000);
    checkTransferStatus();

    return () => {
      isActive = false;
      console.log('clearing interval');
      clearInterval(intervalId);
    };
  }, [customerId, state.resubmitted, state.resubmitting]);

  const handleZipResubmit = async (response) => {
    const formData = {
      CustomerId: customerId,
      zipCode: response.MarketZipCode,
      portInMdn: response.MDN,
      ospAccountNumber: response.OspAccountNo,
      ospAccountPassword: response.OspAccountPass,
      zip: response.MarketZipCode,
      address1: response.Address1,
      city: response.City || 'Miami',
      state: response.State || 'FL',
    };

    try {
      const response = await makeApiRequest(API_ENDPOINTS.ACTIVATION_TRANSFER_UPDATE, formData, 'POST', true);
      console.log(response);

      setState((prev) => ({ ...prev, resubmitted: true }));
    } finally {
      setState((prev) => ({ ...prev, resubmitting: false }));
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, resetting: true, resetError: false }));

    try {
      await makeApiRequest(API_ENDPOINTS.ACTIVATION_RESET, { CustomerId: customerId });
      window.location.reload();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setState((prev) => ({ ...prev, resetting: false, resetError: true }));
    }
  };

  const { checkingStatus, failed, resetting, resetError } = state;

  return html`
    <${Header}
      title="We're working on the transfer!"
      description="It can take up to a minute. Hang tight! We'll let you know when it's done."
    />

    ${
      checkingStatus &&
      html`
        <div
          class="flex gap-4 mb-8 text-lg font-bold font-serif capitalize leading-none bg-white rounded-3xl text-purple px-12 py-6"
        >
          <span class="animate-spin shrink-0 w-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="16" viewBox="0 0 11 16" fill="currentColor">
              <path
                d="M5.92561 1.25962C6.08035 1.31399 6.22086 1.40438 6.33624 1.52378C6.93376 2.11518 7.28176 3.00229 7.54277 3.79673C8.53466 6.82026 8.28418 10.2179 6.57047 12.9141C6.46004 13.0881 5.26021 14.847 5.01928 14.3379C4.97722 14.2497 5.00829 14.1408 5.06708 14.0624C5.75544 13.1463 6.41798 12.2754 6.82477 11.1794C7.39123 9.65158 7.45433 7.97595 7.14792 6.38361C6.99182 5.58337 6.75337 4.80258 6.43662 4.05448C6.28461 3.69225 6.11252 3.33495 5.93948 2.98306C5.76643 2.63118 5.51738 2.32563 5.40409 1.94812C5.35964 1.80027 5.33956 1.63369 5.40027 1.49126C5.44516 1.39305 5.52344 1.31524 5.62026 1.27255C5.71708 1.22986 5.82572 1.22526 5.92561 1.25962Z"
              />
              <path
                d="M9.59264 3.2006C9.69172 3.23769 9.78169 3.29935 9.85557 3.3808C10.2382 3.78424 10.461 4.38939 10.6281 4.93134C11.2632 6.99389 11.1029 9.31162 10.0055 11.1509C9.93484 11.2696 9.16657 12.4695 9.0123 12.1222C8.98537 12.062 9.00526 11.9877 9.04291 11.9343C9.48367 11.3093 9.9079 10.7152 10.1684 9.96753C10.5311 8.92533 10.5715 7.78227 10.3753 6.69602C10.2753 6.15012 10.1227 5.61749 9.91984 5.10717C9.82251 4.86006 9.71232 4.61632 9.60151 4.37628C9.49071 4.13624 9.33124 3.9278 9.2587 3.67027C9.23024 3.56941 9.21738 3.45578 9.25625 3.35862C9.285 3.29163 9.33512 3.23854 9.39712 3.20942C9.45911 3.1803 9.52868 3.17716 9.59264 3.2006Z"
              />
              <path
                d="M1.80313 0.0241547C1.96327 0.0932264 2.10434 0.202091 2.21375 0.341043C2.81128 1.05171 3.1588 2.11524 3.4198 3.06789C4.41122 6.69662 4.16026 10.7733 2.44655 14.0088C2.33565 14.2177 1.13629 16.3285 0.894887 15.7179C0.87776 15.6627 0.873164 15.6042 0.881452 15.5468C0.88974 15.4895 0.910691 15.4349 0.94269 15.3872C1.63056 14.2877 2.29358 13.2419 2.70086 11.9271C3.26731 10.0952 3.33041 8.08296 3.02448 6.17224C2.87018 5.22029 2.63209 4.28481 2.31318 3.37739C2.16117 2.94271 1.98956 2.51394 1.81651 2.09159C1.64347 1.66923 1.39442 1.30306 1.28113 0.850636C1.23667 0.672231 1.2166 0.473619 1.27731 0.302607C1.36861 0.0502758 1.59806 -0.0507555 1.80313 0.0241547Z"
              />
            </svg>
          </span>
          Checking transfer status
        </div>
      `
    }
    ${
      failed &&
      html`
        <div
          class="flex gap-4 mb-8 text-xl font-bold font-serif capitalize leading-none bg-white rounded-3xl text-red-500 px-12 py-6"
        >
          Oops! Something went wrong.
        </div>
      `
    }

    <div class="flex flex-col items-center mt-5">
      <h3 class="h5 mb-2.5">Do you want to start your activation over?</span>
      <${Button} 
        text="Reset Activation" 
        loading=${resetting} 
        onClick=${handleReset} 
        className="btn--sm" 
        arrow=${false}
      />
      ${resetError && html`<p class="text-red-500 text-xs mt-2">We couldn't reset your activation. Please try again.</p>`}
    </div>
  `;
};

export default StepTransferStarted;
